import React, { useState } from "react";
import { ChevronRight, Brain, Users, MessageSquare, ShieldCheck, ArrowLeft, Info, CheckCircle2, AlertTriangle, Lightbulb, X, PlayCircle } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const factors = [
  {
    id: "situational-awareness",
    title: "الوعي الظرفي (Situational Awareness)",
    icon: Brain,
    color: "bg-amber-500",
    lightColor: "bg-amber-50",
    description: "قدرة الطبيب على إدراك وفهم البيئة المحيطة به، وتوقع المخاطر والمضاعفات المستقبلية قبل حدوثها.",
    importance: "يمنع الوقوع في فخ الرؤية النفقية (Tunnel Vision) أو التركيز المفرط على جهاز واحد أو علامة حيوية واحدة وتجاهل التدهور العام لحالة المريض.",
    videoId: "JzlvgtPIof4", // Animated short situational awareness
    tools: [
      { 
        name: "أداة التوقف (Stop & Think)", 
        desc: "التوقف لثوانٍ معدودة قبل اتخاذ قرار حرج لرؤية الصورة الكاملة (Big Picture).",
        instructions: [
          "خذ خطوة للوراء فعلياً أو ذهنياً عن سرير المريض.",
          "اسأل نفسك: هل فاتني شيء؟ هل أركز على العرض وأتجاهل المرض؟",
          "اطلب من الممرض أو المساعد تلخيص حالة المريض في جملتين لتأكيد الفهم المشترك."
        ],
        example: "أثناء محاولة إدخال أنبوب تنفس لمريض بصعوبة، يتوقف الطبيب لعشر ثوانٍ (Stop)، يلاحظ انخفاض الأكسجين الحاد، فيقرر التوقف عن المحاولة والبدء بالتهوية بالكمامة (Think)."
      },
      { 
        name: "قاعدة 10,000 قدم", 
        desc: "التراجع ذهنياً لتقييم الموقف من منظور شامل وعالٍ.",
        instructions: [
          "عندما تشعر بالتوتر أو ضياع السيطرة، تخيل أنك تنظر للغرفة من الأعلى.",
          "قم بتقييم الموارد المتاحة (من في الغرفة؟ ماذا يفعل كل شخص؟).",
          "أعد توزيع المهام لتجنب تكدس الفريق حول إجراء واحد."
        ],
        example: "طبيب الطوارئ يلاحظ أن 3 ممرضين يحاولون سحب دم من مريض واحد، بينما لا أحد يراقب أجهزة الإنعاش، فيتدخل لتوزيع المهام بناءً على رؤيته الشاملة."
      }
    ]
  },
  {
    id: "communication",
    title: "التواصل الفعال (Communication)",
    icon: MessageSquare,
    color: "bg-blue-500",
    lightColor: "bg-blue-50",
    description: "نقل المعلومات الطبية بدقة ووضوح بين أفراد الطاقم الطبي، وبين الطبيب والمريض.",
    importance: "أكثر من 70% من الأخطاء الطبية الجسيمة ناتجة عن سوء التواصل أثناء تسليم المناوبات أو أثناء الحالات الحرجة.",
    videoId: "bxr2k2zBJuo", // Animated SBAR short
    tools: [
      { 
        name: "SBAR", 
        desc: "أداة لتنظيم المعلومات أثناء المحادثات الطبية الطارئة.",
        instructions: [
          "S (Situation) الموقف: عرّف بنفسك والمريض وسبب الاتصال بإيجاز.",
          "B (Background) الخلفية: اذكر التاريخ المرضي ذو الصلة المباشرة بالمشكلة.",
          "A (Assessment) التقييم: ما هو تشخيصك أو تقييمك للمشكلة الحالية؟",
          "R (Recommendation) التوصية: ما الذي تحتاجه من الطبيب الآخر تحديداً؟"
        ],
        example: "مرحباً دكتور، أنا أحمد من الطوارئ. (S) أتصل بخصوص المريض خالد الذي يعاني من ألم حاد بالصدر. (B) المريض لديه تاريخ جلطة قلبية سابقة. (A) أظن أنه يعاني من احتشاء جديد. تخطيط القلب يظهر تغيرات. (R) أرجو قدومك فوراً للتقييم لإعطاء مسيلات الدم."
      },
      { 
        name: "التواصل المغلق (Closed-loop)", 
        desc: "إعادة ترديد الأمر الطبي للتأكد من فهمه بشكل صحيح.",
        instructions: [
          "يصدر الطبيب الأمر بصوت واضح مع ذكر اسم الشخص المعني.",
          "يردد المتلقي الأمر بصوت مسموع لتأكيد الفهم.",
          "يؤكد الطبيب أن ما سمعه هو ما قصده فعلاً."
        ],
        example: "الطبيب: (يا محمد، أعطِ المريض 1 مل من الأدرينالين وريدياً). محمد: (1 مل أدرينالين وريدياً، جاري الإعطاء). الطبيب: (نعم، صحيح)."
      },
      { 
        name: "I PASS the BATON", 
        desc: "أداة متقدمة لتسليم الحالات المعقدة بين الأقسام.",
        instructions: [
          "Introduction: التعريف بالنفس وبالمريض.",
          "Patient: بيانات المريض والعمر والجنس.",
          "Assessment: المشكلة الحالية والعلامات الحيوية.",
          "Situation: الحالة الحالية (مستقر، حرج).",
          "Safety Concerns: مخاوف السلامة (حساسية، سقوط).",
          "the BATON: (Background, Actions, Timing, Ownership, Next)."
        ],
        example: "تستخدم هذه الأداة بشكل أساسي عند نقل مريض من العناية المركزة إلى قسم الجراحة لضمان عدم ضياع أي تفصيل طبي هام."
      },
      { 
        name: "منهجية ABCDE", 
        desc: "أداة لتوحيد لغة الفريق الطبي لتقييم ومعالجة الحالات الطارئة بشكل تسلسلي.",
        instructions: [
          "A - Airway: التأكد من مجرى الهواء (مع حماية العنق).",
          "B - Breathing: تقييم التنفس وإعطاء الأكسجين إذا لزم.",
          "C - Circulation: تقييم الدورة الدموية، النبض، النزيف، وفتح خط وريدي.",
          "D - Disability: التقييم العصبي (مستوى الوعي، سكر الدم).",
          "E - Exposure: كشف المريض بالكامل مع الحفاظ على حرارته وكرامته."
        ],
        example: "ممرض: (دكتور، قمنا بتقييم المريض حسب ABCDE. المجرى مفتوح (A)، لكن التنفس سريع جداً 30/دقيقة والأكسجين 85% (B). الضغط مستقر (C)، وهو واعي تماماً (D). نطلب حضورك للتقييم السريع)."
      },
      { 
        name: "Read Back & Verify (القراءة العكسية والتحقق)", 
        desc: "أداة أساسية لتلقي الأوامر الطبية الشفهية أو الهاتفية لمنع أخطاء الأدوية.",
        instructions: [
          "تلقي الأمر: استمع للأمر الطبي أو نتائج التحاليل واكتبها فوراً.",
          "القراءة العكسية (Read Back): اقرأ ما كتبته حرفياً للطبيب الذي أصدر الأمر.",
          "التحقق (Verify): يجب أن يؤكد الطبيب صراحةً أن ما قرأته صحيح (مثل قول: نعم، صحيح)."
        ],
        example: "الطبيب عبر الهاتف: (أعطِ المريض 5 ملغ مورفين وريدياً). الممرض: يكتب الأمر ثم يقول (دكتور، لقد كتبت: إعطاء المريض خالد 5 ملغ مورفين وريدياً، هل هذا صحيح؟). الطبيب: (نعم صحيح)."
      },
      { 
        name: "Teach Back (إعادة الشرح من المريض)", 
        desc: "طريقة للتأكد من فهم المريض أو عائلته للتعليمات الطبية لضمان سلامته في المنزل.",
        instructions: [
          "اشرح المعلومات الطبية للمريض بلغة بسيطة ومفهومة.",
          "اطلب من المريض إعادة شرح ما فهمه بأسلوبه الخاص.",
          "إذا كان هناك سوء فهم، أعد الشرح بطريقة مختلفة، ثم اطلب منه إعادة الشرح مرة أخرى حتى تتأكد 100%."
        ],
        example: "الطبيب: (أريد التأكد من أنني شرحت لك طريقة أخذ دواء السكر بشكل واضح. هل يمكنك أن تخبرني كيف ومتى ستأخذ هذا الدواء في المنزل؟). المريض: (سآخذ حبة واحدة في الصباح بعد الفطور). الطبيب: (ممتاز جداً)."
      }
    ]
  },
  {
    id: "teamwork",
    title: "العمل الجماعي (Teamwork)",
    icon: Users,
    color: "bg-emerald-500",
    lightColor: "bg-emerald-50",
    description: "عمل الكادر الطبي كفريق واحد متناغم ومترابط بدلاً من العمل كأفراد منعزلين.",
    importance: "يوزع عبء العمل الإدراكي (Cognitive Load)، ويقلل من الإرهاق الفردي، ويسرع من عملية إنقاذ المريض بفضل تضافر الجهود.",
     // Improving Human Factors
    tools: [
      { 
        name: "قاعدة التحدي المزدوج (Two-Challenge Rule)", 
        desc: "يحق لأي عضو إيقاف الإجراء إذا تم التعبير عن مخاوفه مرتين دون استجابة.",
        instructions: [
          "التحدي الأول: اذكر مخاوفك للطبيب المسؤول بوضوح (مثال: دكتور، أظن أن الجرعة زائدة).",
          "إذا لم يستجب أو لم يتوقف، قم بالتحدي الثاني بشكل أقوى وأكثر حزماً.",
          "إذا تم تجاهلك مرة أخرى، يحق لك إيقاف الإجراء واستدعاء مسؤول أعلى (حسب البروتوكول)."
        ],
        example: "الممرض: دكتور، ضغط المريض ينخفض جداً. (لا توجد استجابة). الممرض بصوت أعلى: دكتور أحمد! ضغط المريض 70/40، يجب أن نتوقف ونعطي سوائل الآن!"
      },
      { 
        name: "Brief / Huddle / Debrief", 
        desc: "الاجتماعات المصغرة قبل، أثناء، وبعد التعامل مع الحالات.",
        instructions: [
          "Brief (قبل): اجتماع قصير لمدة دقيقتين قبل بدء العمليات لتوزيع الأدوار وتوقع المشاكل.",
          "Huddle (أثناء): اجتماع طارئ في منتصف الحدث لإعادة التقييم إذا تغيرت حالة المريض.",
          "Debrief (بعد): نقاش مريح بعد انتهاء الحالة لمعرفة ما سار بشكل جيد وما يجب تحسينه."
        ],
        example: "بعد عملية إنعاش قلبي رئوي ناجحة، يجتمع الفريق لثلاث دقائق (Debrief): ماذا فعلنا جيداً؟ التدليك كان ممتازاً. ماذا نطور؟ تأخرنا في إحضار جهاز الصدمات، يجب وضعه أقرب للسرير مستقبلاً."
      }
    ]
  },
  {
    id: "psychological-safety",
    title: "السلامة النفسية (Psychological Safety)",
    icon: ShieldCheck,
    color: "bg-purple-500",
    lightColor: "bg-purple-50",
    description: "خلق بيئة عمل يشعر فيها جميع أفراد الفريق بالأمان التام للتحدث عن الأخطاء، أو طرح الأسئلة، أو طلب المساعدة.",
    importance: "تكسر حاجز الخوف من التسلسل الهرمي الصارم (Steep Hierarchy)، مما يسمح مثلاً للممرض بتنبيه الطبيب الجراح بخطأ محتمل دون الخوف من التوبيخ، مما ينقذ حياة المريض.",
    videoId: "LhoLuui9gX8", // Mica Endsley
    tools: [
      { 
        name: "التحدث الحر (Speak Up)", 
        desc: "تشجيع ثقافة تقبل النقد والتوجيه من جميع أفراد الفريق مهما كانت رتبتهم.",
        instructions: [
          "على القائد (الاستشاري أو الأخصائي) أن يقول بوضوح: أنا أخطئ، أرجوكم صححوني إذا رأيتم شيئاً غير منطقي.",
          "يجب شكر أي شخص يطرح سؤالاً أو يوقف خطأً محتملاً، بدلاً من تجاهله أو توبيخه.",
          "التركيز على (ما هو الخطأ) وليس (من المخطئ)."
        ],
        example: "الجراح أثناء العملية: (يا شباب، هذا الإجراء معقد قليلاً، إذا رأى أحدكم أي شيء غير طبيعي أرجو أن يتحدث فوراً). هذا التصريح البسيط يرفع السلامة النفسية للفريق بأكمله."
      },
      { 
        name: "CUS Words", 
        desc: "استخدام كلمات محددة للتعبير عن القلق للفت الانتباه الفوري.",
        instructions: [
          "C - I am Concerned: (أنا قلق) استخدمها كأول تنبيه ناعم.",
          "U - I am Uncomfortable: (أنا غير مرتاح لهذا الإجراء) استخدمها إذا لم يتغير شيء.",
          "S - This is a Safety issue: (هذه مشكلة تهدد سلامة المريض) الكلمة الحمراء لإيقاف كل شيء."
        ],
        example: "- دكتور، أنا قلقة (Concerned) بشأن هذا الدواء مع حالة المريض المكتوبة بالملف.\n- دكتور، أنا غير مرتاحة (Uncomfortable) لإعطاء هذا الدواء، أرجو مراجعة الملف.\n- دكتور، إعطاء هذا الدواء الآن يعتبر تهديداً للسلامة (Safety Issue)، سنتوقف ونراجع الصيدلي."
      }
    ]
  }
];

export default function HumanFactorsViewer({ onBack }: { onBack: () => void }) {
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<any | null>(null);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16 md:pt-24 pb-8 md:pb-12 px-4 relative overflow-hidden" dir="rtl">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-[50vh] md:h-96 bg-teal-900 rounded-b-[4rem] z-0" />
      
      <div className="max-w-6xl mx-auto w-full relative z-10 flex-1 flex flex-col">
        <button 
          onClick={onBack}
          className="self-end mb-8 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 px-5 flex items-center gap-2 transition-all border border-white/20 backdrop-blur-md"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-sm font-bold">العودة للبوابة</span>
        </button>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-teal-500/20 rounded-full mb-4">
            <Brain className="w-10 h-10 text-teal-300" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">العوامل البشرية في الطب</h1>
          <p className="text-teal-100 max-w-2xl mx-auto text-lg leading-relaxed">
            (Human Factors in Healthcare)
            <br />
            الركائز الأساسية لتقليل الأخطاء الطبية، تحسين جودة الرعاية، ورفع كفاءة الأطباء في بيئة العمل السريرية.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 relative z-20">
          {factors.map((factor, idx) => {
            const Icon = factor.icon;
            const isActive = activeCard === factor.id;
            
            return (
              <motion.div 
                key={factor.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => {
                  if (activeCard !== factor.id) {
                    setActiveCard(factor.id);
                    setActiveTool(null); // Reset tool selection when switching cards
                  } else {
                    setActiveCard(null);
                    setActiveTool(null);
                  }
                }}
                className={`bg-white rounded-3xl p-1 transition-all duration-500 cursor-pointer ${isActive ? 'ring-4 ring-teal-500/30 shadow-2xl scale-[1.02] md:col-span-2' : 'shadow-xl hover:shadow-2xl hover:-translate-y-1'}`}
              >
                <div className="bg-white rounded-[1.35rem] h-full overflow-hidden border border-slate-100">
                  <div className={`p-4 md:p-6 ${factor.lightColor} flex items-center gap-4`}>
                    <div className={`w-12 h-12 md:w-14 md:h-14 ${factor.color} rounded-2xl flex items-center justify-center shadow-lg text-white`}>
                      <Icon className="w-6 h-6 md:w-7 md:h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-slate-800">{factor.title}</h3>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 ${isActive ? 'rotate-90 bg-white/50 text-slate-800' : 'bg-white text-slate-400'}`}>
                      <ArrowLeft className="w-5 h-5" />
                    </div>
                  </div>
                  
                  <div className="p-4 md:p-6">
                    <p className="text-slate-600 text-lg leading-relaxed mb-6 font-medium">
                      {factor.description}
                    </p>
                    
                    <AnimatePresence>
                      {isActive && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                          onClick={(e) => e.stopPropagation()} // Prevent card collapse when interacting with tools
                        >
                          <div className="pt-6 border-t border-slate-100 space-y-6">
                            
                            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                              <h4 className="flex items-center gap-2 font-bold text-slate-800 mb-3">
                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                                أهميتها في عمل الطبيب:
                              </h4>
                              <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                                {factor.importance}
                              </p>
                            </div>

                            {factor.videoId && (
                              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 shadow-inner">
                                <h4 className="flex items-center gap-2 font-bold text-slate-800 mb-3">
                                  <PlayCircle className="w-5 h-5 text-rose-500" />
                                  فيديو تعليمي:
                                </h4>
                                <div className="aspect-video w-full rounded-xl overflow-hidden shadow-sm">
                                  <iframe 
                                    width="100%" 
                                    height="100%" 
                                    src={`https://www.youtube.com/embed/${factor.videoId}`}
                                    title="YouTube video player" 
                                    frameBorder="0" 
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                    allowFullScreen
                                  ></iframe>
                                </div>
                              </div>
                            )}
                            
                            <div>
                              <h4 className="flex items-center gap-2 font-bold text-slate-800 mb-4">
                                <CheckCircle2 className="w-5 h-5 text-teal-500" />
                                أدوات واستراتيجيات عملية (اضغط على الأداة للتفاصيل):
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {factor.tools.map((tool, i) => {
                                  const isToolActive = activeTool?.name === tool.name;
                                  
                                  return (
                                    <div 
                                      key={i} 
                                      onClick={() => setActiveTool(isToolActive ? null : tool)}
                                      className={`border rounded-xl p-4 transition-all cursor-pointer ${isToolActive ? 'border-teal-500 bg-teal-50 shadow-md ring-2 ring-teal-500/20' : 'border-slate-200 bg-white hover:border-teal-300 shadow-sm hover:shadow-md'}`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <h5 className="font-bold text-slate-800 mb-2 flex items-center gap-2" dir="ltr">
                                          <span className={`w-2 h-2 rounded-full ${isToolActive ? 'bg-teal-600' : 'bg-teal-400'}`}></span>
                                          {tool.name}
                                        </h5>
                                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isToolActive ? 'rotate-90 text-teal-600' : 'rotate-180'}`} />
                                      </div>
                                      <p className="text-slate-500 text-sm leading-relaxed">
                                        {tool.desc}
                                      </p>
                                      
                                      <AnimatePresence>
                                        {isToolActive && (
                                          <motion.div
                                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                            className="overflow-hidden"
                                          >
                                            <div className="pt-4 border-t border-teal-200/50">
                                              <h6 className="text-sm font-bold text-teal-800 mb-2 flex items-center gap-1.5">
                                                <Info className="w-3.5 h-3.5" /> كيفية الاستخدام:
                                              </h6>
                                              <ul className="list-disc list-inside text-sm text-slate-700 space-y-1.5 mb-4">
                                                {tool.instructions.map((inst, j) => (
                                                  <li key={j} className="leading-relaxed">{inst}</li>
                                                ))}
                                              </ul>
                                              
                                              <div className="bg-white rounded-lg p-3 border border-amber-100 shadow-sm relative">
                                                <div className="absolute top-3 left-3 text-amber-200">
                                                  <Lightbulb className="w-5 h-5" />
                                                </div>
                                                <h6 className="text-xs font-bold text-amber-700 mb-1">مثال سريري واقعي:</h6>
                                                <p className="text-sm text-slate-600 leading-relaxed italic pr-2 border-r-2 border-amber-400 whitespace-pre-line">
                                                  {tool.example}
                                                </p>
                                              </div>
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
